import type { ImportRow } from './api';

/**
 * CSV → baris impor (§G11.3), **diurai di browser**.
 *
 * Berkasnya tidak pernah menyeberang ke server: yang dikirim hasil uraiannya.
 * Berkas CSV berisi password kamera karena itu tidak pernah ditulis ke disk
 * server, tidak masuk log akses, dan tidak tertinggal di direktori sementara
 * yang tidak disapu siapa pun. Ia juga yang membuat "baris bermasalah bisa
 * diperbaiki di tempat" mungkin — barisnya memang sudah ada di tabel yang
 * sedang dilihat orangnya.
 */

/** Kolom yang dikenal. Header dicocokkan menurut nama ini, bukan urutannya. */
export const KOLOM_CSV = [
  'name',
  'host',
  'port',
  'httpPort',
  'httpSecure',
  'transport',
  'mainPath',
  'subPath',
  'username',
  'password',
  'audioEnabled',
  'recordEnabled',
  'maxDay',
  'segmentSeconds',
  'isEnabled',
  'template',
  'tags',
] as const;

/** Nama kolom yang lazim ditulis orang, dipetakan ke kunci resmi di atas. */
const ALIAS: Record<string, (typeof KOLOM_CSV)[number]> = {
  nama: 'name',
  ip: 'host',
  rtsp_port: 'port',
  http_port: 'httpPort',
  http_secure: 'httpSecure',
  main_path: 'mainPath',
  sub_path: 'subPath',
  audio: 'audioEnabled',
  audio_enabled: 'audioEnabled',
  record: 'recordEnabled',
  record_enabled: 'recordEnabled',
  retention: 'maxDay',
  max_day: 'maxDay',
  segment: 'segmentSeconds',
  segment_seconds: 'segmentSeconds',
  enabled: 'isEnabled',
  is_enabled: 'isEnabled',
  tag: 'tags',
};

export interface HasilUrai {
  rows: ImportRow[];
  /** Kolom di berkas yang tidak dikenal — disebutkan, bukan dibuang diam-diam. */
  kolomTidakDikenal: string[];
  galat: string | null;
}

/**
 * Pemisah dideteksi dari **baris header**, bukan ditebak dari seluruh berkas.
 *
 * Excel berlokal Indonesia menyimpan CSV dengan titik koma, dan itu berkas yang
 * paling mungkin datang ke sini. Menebaknya dari baris data akan salah pada
 * berkas yang path RTSP-nya memuat koma di query string.
 */
function pisahkan(baris: string, pemisah: string): string[] {
  const out: string[] = [];
  let sekarang = '';
  let dalamKutip = false;

  for (let i = 0; i < baris.length; i++) {
    const c = baris[i]!;
    if (dalamKutip) {
      // `""` di dalam kutipan berarti satu tanda kutip, bukan penutup.
      if (c === '"' && baris[i + 1] === '"') {
        sekarang += '"';
        i++;
      } else if (c === '"') {
        dalamKutip = false;
      } else {
        sekarang += c;
      }
      continue;
    }
    if (c === '"') dalamKutip = true;
    else if (c === pemisah) {
      out.push(sekarang);
      sekarang = '';
    } else sekarang += c;
  }
  out.push(sekarang);
  return out.map((s) => s.trim());
}

function bool(v: string): boolean | undefined {
  const s = v.trim().toLowerCase();
  if (['1', 'true', 'ya', 'yes', 'y'].includes(s)) return true;
  if (['0', 'false', 'tidak', 'no', 'n'].includes(s)) return false;
  return undefined;
}

function angka(v: string): number | null | undefined {
  const s = v.trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function uraiCsv(teks: string): HasilUrai {
  // BOM UTF-8 dibuang. Excel menulisnya; tanpa dibuang, kolom PERTAMA tidak
  // pernah cocok dengan nama apa pun dan pesannya membingungkan — "kolom `name`
  // tidak ada" padahal jelas ada di layar.
  const bersih = teks.replace(/^﻿/, '');
  const baris = bersih.split(/\r?\n/).filter((b) => b.trim() !== '');
  if (baris.length < 2) {
    return { rows: [], kolomTidakDikenal: [], galat: 'The file has no data rows.' };
  }

  const pemisah = (baris[0]!.match(/;/g)?.length ?? 0) > (baris[0]!.match(/,/g)?.length ?? 0) ? ';' : ',';
  const header = pisahkan(baris[0]!, pemisah);

  const kolomTidakDikenal: string[] = [];
  const peta = header.map((h) => {
    const kunci = h.replace(/\s+/g, '');
    const langsung = KOLOM_CSV.find((k) => k.toLowerCase() === kunci.toLowerCase());
    if (langsung) return langsung;
    const alias = ALIAS[h.trim().toLowerCase().replace(/\s+/g, '_')];
    if (alias) return alias;
    if (h !== '') kolomTidakDikenal.push(h);
    return null;
  });

  if (!peta.includes('name') || !peta.includes('host')) {
    return {
      rows: [],
      kolomTidakDikenal,
      galat: 'The file needs at least a “name” and a “host” column.',
    };
  }

  const rows: ImportRow[] = [];
  for (const b of baris.slice(1)) {
    const sel = pisahkan(b, pemisah);
    const row: Record<string, unknown> = {};
    peta.forEach((kunci, i) => {
      if (!kunci) return;
      const nilai = sel[i] ?? '';
      if (nilai === '' && kunci !== 'subPath' && kunci !== 'username') return;
      switch (kunci) {
        case 'port':
        case 'httpPort':
        case 'maxDay':
        case 'segmentSeconds':
          row[kunci] = angka(nilai);
          break;
        case 'httpSecure':
        case 'audioEnabled':
        case 'recordEnabled':
        case 'isEnabled':
          row[kunci] = bool(nilai);
          break;
        case 'tags':
          // Beberapa tag dalam satu sel, dipisah koma atau titik koma — apa pun
          // yang tidak dipakai sebagai pemisah kolom di berkas ini.
          row[kunci] = nilai
            .split(/[,;]/)
            .map((s) => s.trim())
            .filter((s) => s !== '');
          break;
        default:
          row[kunci] = nilai === '' ? null : nilai;
      }
    });
    // `host` boleh kosong di berkas dan tetap masuk daftar: validator server
    // yang menolaknya dengan kalimat yang menyebut barisnya, dan barisnya harus
    // ada di tabel supaya bisa diperbaiki di tempat (§G11.3).
    if (row.name) rows.push(row as unknown as ImportRow);
  }

  return { rows, kolomTidakDikenal, galat: null };
}

/**
 * Berkas contoh, diunduh dari layar pertama. Format yang harus ditebak akan
 * ditebak salah — dan yang paling sering salah ditebak adalah kolom mana yang
 * wajib.
 */
export function contohCsv(): string {
  const header = KOLOM_CSV.join(',');
  const baris = [
    'Gerbang Utama,10.20.1.11,554,80,0,tcp,/Streaming/Channels/101,/Streaming/Channels/102,admin,,0,1,,,1,,outdoor;critical',
    'Lobi Lantai 2,10.20.1.12,554,80,0,tcp,/cam/realmonitor?channel=1&subtype=0,,admin,,0,1,14,900,1,Kamera POP,fasum',
  ];
  return `﻿${header}\n${baris.join('\n')}\n`;
}

/** Baris yang gagal, dikembalikan sebagai CSV untuk diperbaiki lalu diunggah lagi. */
export function barisKeCsv(rows: ImportRow[]): string {
  const sel = (v: unknown): string => {
    let s = v === null || v === undefined ? '' : Array.isArray(v) ? v.join(';') : String(v);
    // Sel diawali `=`, `+`, `-`, atau `@` diberi kutip tunggal di depan — tanpa
    // itu Excel memperlakukannya sebagai formula (aturan yang sama dengan
    // export CSV log, §D7.3).
    if (/^[=+\-@]/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };
  const isi = rows.map((r) => KOLOM_CSV.map((k) => sel((r as unknown as Record<string, unknown>)[k])).join(','));
  return `﻿${KOLOM_CSV.join(',')}\n${isi.join('\n')}\n`;
}
